import React, { useEffect, useRef } from "react";
import { BaseIndicatorProps } from "../../lib/interface/Indicator.interface";
import {
  AuctionMarketEvent,
  AmtLiquidityType,
} from "../../lib/interface/AuctionMarketEvent.interface";
import {
  createSeriesMarkers,
  Time,
  ISeriesMarkersPluginApi,
} from "lightweight-charts";

/**
 * Props specific to Auction Market Theory indicator
 */
export interface AuctionMarketIndicatorProps extends BaseIndicatorProps {
  auctionMarketEvents: AuctionMarketEvent[];
  formatTime: (timestamp: number) => number;
  /**
   * Minimum confidence threshold (0-1) for displaying signals.
   * Higher values filter to only high-quality signals for better backtest performance.
   */
  minConfidence?: number;
  /**
   * Only show signals aligned with higher timeframe trend
   */
  trendAlignedOnly?: boolean;
  buyColor?: string;
  sellColor?: string;
}

/**
 * Map liquidity type to display text for tooltip context
 */
const liquidityTypeLabels: Record<AmtLiquidityType, string> = {
  [AmtLiquidityType.STOP_RUN]: "SR",
  [AmtLiquidityType.LIQUIDITY_GRAB]: "LG",
  [AmtLiquidityType.FLIP]: "FL",
  [AmtLiquidityType.MAGNET]: "MG",
  [AmtLiquidityType.BARRIER]: "BR",
};

/**
 * Auction Market Theory Indicator Component
 * Shows buy/sell signal markers on the chart based on AMT analysis:
 * - Stop runs / liquidity sweeps at swing highs/lows
 * - Support/resistance flips
 * - Barrier rejections at value area boundaries
 * - Liquidity grabs with high volume climax
 */
export const AuctionMarketIndicator: React.FC<AuctionMarketIndicatorProps> = ({
  chartInstance,
  candleSeries,
  isEnabled,
  auctionMarketEvents,
  formatTime,
  minConfidence = 0.5,
  trendAlignedOnly = false,
  buyColor,
  sellColor,
}) => {
  const seriesMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const buyColorValue = buyColor ?? "#10b981"; // Emerald green for buys
  const sellColorValue = sellColor ?? "#f43f5e"; // Rose red for sells

  // Initialize the series markers API ONCE when component mounts
  useEffect(() => {
    if (!chartInstance || !candleSeries) {
      return;
    }

    // Create the series markers API reference only once
    if (!seriesMarkersRef.current) {
      try {
        seriesMarkersRef.current = createSeriesMarkers(candleSeries, []);
      } catch {
        return;
      }
    }
  }, [chartInstance, candleSeries]);

  // Update markers when auctionMarketEvents or isEnabled changes
  useEffect(() => {
    if (!chartInstance || !candleSeries || !seriesMarkersRef.current) {
      return;
    }

    // If disabled, clear markers
    if (!isEnabled) {
      try {
        seriesMarkersRef.current.setMarkers([]);
      } catch {}
      return;
    }

    // If no events to display, clear markers
    if (auctionMarketEvents.length === 0) {
      try {
        seriesMarkersRef.current.setMarkers([]);
      } catch {}
      return;
    }

    // Filter signals based on quality parameters for better performance
    const filteredSignals = auctionMarketEvents.filter((event) => {
      // Filter by confidence threshold
      if (event.confidence < minConfidence) {
        return false;
      }

      // Filter by trend alignment if requested
      if (trendAlignedOnly && !event.isHighTimeframeTrendAligned) {
        return false;
      }

      return true;
    });

    // Create markers for filtered signals
    const chartMarkers = filteredSignals.map((event) => {
      const isBuy = event.action === "BUY";
      const liquidityLabel = liquidityTypeLabels[event.liquidityType] || "";

      // Color intensity based on confidence
      // Higher confidence = more saturated color
      const alpha = Math.min(1, 0.5 + event.confidence * 0.5);
      const color = isBuy
        ? adjustColorAlpha(buyColorValue, alpha)
        : adjustColorAlpha(sellColorValue, alpha);

      return {
        time: formatTime(event.timestamp) as Time,
        position: isBuy ? ("belowBar" as const) : ("aboveBar" as const),
        color,
        shape: isBuy ? ("arrowUp" as const) : ("arrowDown" as const),
        text: liquidityLabel,
      };
    });

    // Use setMarkers to update the existing markers API
    try {
      seriesMarkersRef.current.setMarkers(chartMarkers);
    } catch {}
  }, [
    auctionMarketEvents,
    isEnabled,
    formatTime,
    chartInstance,
    candleSeries,
    minConfidence,
    trendAlignedOnly,
    buyColorValue,
    sellColorValue,
  ]);

  // Cleanup effect to clear markers when component unmounts
  useEffect(() => {
    return () => {
      if (seriesMarkersRef.current) {
        try {
          seriesMarkersRef.current.setMarkers([]);
        } catch {}
      }
    };
  }, []);

  // This is a logic-only component
  return null;
};

/**
 * Adjust hex color alpha/opacity
 */
function adjustColorAlpha(hexColor: string, _alpha: number): string {
  // For simplicity, just return the color as-is since lightweight-charts
  // handles marker colors without alpha channel
  // A more sophisticated implementation would convert to rgba
  return hexColor;
}
